import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, FileUp, CheckCircle, AlertCircle, Clock, User, Mail } from 'lucide-react';

// D1 API URL
const API_URL = 'https://happyhumantools-d1-api.b-9f2.workers.dev';

interface UploadLink {
  id: string;
  slug: string;
  name: string;
  description?: string;
  created_by: string;
  expires_at: string;
  max_files: number;
  max_size_mb: number;
  files_uploaded: number;
  status: string;
}

export const D1PublicUploadPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [linkInfo, setLinkInfo] = useState<UploadLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploaderName, setUploaderName] = useState('');
  const [uploaderEmail, setUploaderEmail] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(() => {
    fetchLinkInfo();
  }, [slug]);

  const fetchLinkInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/upload-links/slug/${slug}`);
      if (!response.ok) {
        throw new Error('Upload link not found');
      }
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Link not found');
      }

      // Check if link is expired
      const link = data.data;
      const now = new Date();
      const expires = new Date(link.expires_at);

      if (expires < now) {
        throw new Error('This upload link has expired');
      }

      if (link.files_uploaded >= link.max_files) {
        throw new Error('This upload link has reached its file limit');
      }

      setLinkInfo(link);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load upload link');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !linkInfo) return;

    const files = e.target.files;
    const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
    const maxSizeBytes = linkInfo.max_size_mb * 1024 * 1024;

    if (totalSize > maxSizeBytes) {
      alert(`Total file size exceeds ${linkInfo.max_size_mb}MB limit`);
      return;
    }

    if (files.length + linkInfo.files_uploaded > linkInfo.max_files) {
      alert(`This link only accepts ${linkInfo.max_files - linkInfo.files_uploaded} more file(s)`);
      return;
    }

    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (!selectedFiles || !linkInfo || !uploaderName || !uploaderEmail) {
      alert('Please fill in your information and select files');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Upload each file to R2 via the worker endpoint
      const uploadPromises = Array.from(selectedFiles).map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('uploaderName', uploaderName);
        formData.append('uploaderEmail', uploaderEmail);

        const response = await fetch(`${API_URL}/api/upload-links/${slug}/upload`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Upload failed');
        }

        return response.json();
      });

      const results = await Promise.all(uploadPromises);

      setUploadSuccess(true);

      // Log successful uploads
      console.log('Files uploaded successfully:', {
        uploaderName,
        uploaderEmail,
        files: results.map(r => r.data),
        linkSlug: slug
      });

    } catch (err) {
      setError('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading upload link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">Upload Link Error</h2>
          <p className="text-gray-600 text-center">{error}</p>
        </div>
      </div>
    );
  }

  if (uploadSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Successful!</h2>
          <p className="text-gray-600 mb-4">
            Your files have been uploaded successfully.
          </p>
          <p className="text-sm text-gray-500">
            The owner of this link has been notified.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {linkInfo?.name || 'File Upload'}
            </h1>
            {linkInfo?.description && (
              <p className="text-gray-600">{linkInfo.description}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-blue-50 rounded-lg p-4">
              <FileUp className="w-8 h-8 text-blue-600 mb-2" />
              <div className="text-sm text-gray-600">Files Allowed</div>
              <div className="text-xl font-bold text-gray-900">
                {linkInfo ? `${linkInfo.max_files - linkInfo.files_uploaded} / ${linkInfo.max_files}` : '-'}
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <Upload className="w-8 h-8 text-green-600 mb-2" />
              <div className="text-sm text-gray-600">Max Size</div>
              <div className="text-xl font-bold text-gray-900">
                {linkInfo?.max_size_mb || 0} MB
              </div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <Clock className="w-8 h-8 text-orange-600 mb-2" />
              <div className="text-sm text-gray-600">Expires</div>
              <div className="text-xl font-bold text-gray-900">
                {linkInfo ? new Date(linkInfo.expires_at).toLocaleDateString() : '-'}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Your Name
              </label>
              <input
                type="text"
                value={uploaderName}
                onChange={(e) => setUploaderName(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter your name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Your Email
              </label>
              <input
                type="email"
                value={uploaderEmail}
                onChange={(e) => setUploaderEmail(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileUp className="w-4 h-4 inline mr-1" />
                Select Files
              </label>
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                accept="*/*"
              />
              {selectedFiles && (
                <p className="mt-2 text-sm text-gray-600">
                  {selectedFiles.length} file(s) selected
                </p>
              )}
            </div>

            <button
              onClick={handleUpload}
              disabled={uploading || !selectedFiles || !uploaderName || !uploaderEmail}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {uploading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Uploading...
                </span>
              ) : (
                'Upload Files'
              )}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg">
              {error}
            </div>
          )}
        </div>

        <div className="mt-4 text-center text-sm text-gray-500">
          Powered by CloudFlare D1 & R2 Storage
        </div>
      </div>
    </div>
  );
};